import { message, Spin } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { getTeamGroups } from '../../../api/aops.api'
import { getCalendarAdminWorkItems } from '../../../api/standalone.api'
import { menuSelectedTeam } from '../../../store/aops'
import {
  scheduleBoardRefetchTrigger,
  trackerBoardRefetchTrigger,
} from '../../../store/standaloneSchedule'
import { userState } from '../../../store/user'
import '../StandaloneSchedulePage.scss'
// import SectionHeader from '../ui/SectionHeader'
import SectionHeader from '../../../components/apos/SectionHeader'
import TaskColorChip from '../ui/TaskColorChip'
import StandaloneRoutineItemPopup from './StandaloneRoutineItemPopup'
import StandaloneScheduledTaskPopup from './StandaloneScheduledTaskPopup'

const ScheduleBoardAdminSection = ({ title, subtitle }) => {
  const user = useRecoilValue(userState)
  const scheduleBoardRefetch = useRecoilValue(scheduleBoardRefetchTrigger)
  const setScheduleBoardRefetch = useSetRecoilState(scheduleBoardRefetchTrigger)
  const setTrackerBoardRefetch = useSetRecoilState(trackerBoardRefetchTrigger)
  const [currentDate, setCurrentDate] = useState(dayjs())
  const [tasks, setTasks] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTeam, setSelectedTeam] = useRecoilState(menuSelectedTeam)
  const [teamGroupId, setTeamGroupId] = useState(null)
  // 모달 상태
  const [selectedRoutineItemId, setSelectedRoutineItemId] = useState(null)
  const [selectedScheduledItemId, setSelectedScheduledItemId] = useState(null)
  const [isRoutineItemPopupOpen, setIsRoutineItemPopupOpen] = useState(false)
  const [isScheduledItemPopupOpen, setIsScheduledItemPopupOpen] =
    useState(false)

  useEffect(() => {
    const findTeamGroupId = async () => {
      if (!selectedTeam) {
        setTeamGroupId(null)
        return
      }

      try {
        const teamGroups = await getTeamGroups()
        if (teamGroups && Array.isArray(teamGroups)) {
          const foundTeam = teamGroups.find(
            (team) => team.name === selectedTeam,
          )
          if (foundTeam) {
            setTeamGroupId(foundTeam.id)
          } else {
            console.warn(
              'selectedTeam에 해당하는 팀을 찾을 수 없습니다:',
              selectedTeam,
            )
            setTeamGroupId(null)
          }
        }
      } catch (error) {
        console.error('getTeamGroups error::', error)
        setTeamGroupId(null)
      }
    }

    findTeamGroupId()
  }, [selectedTeam])

  const fetchCalendarTasks = async () => {
    if (!teamGroupId) {
      // console.log('teamGroupId가 없어서 API를 호출하지 않습니다.')
      return
    }

    setIsLoading(true)
    try {
      const year = currentDate.year()
      const month = currentDate.month() + 1 // dayjs month는 0-11
      const res = await getCalendarAdminWorkItems({
        year,
        month,
        teamGroupId,
      })
      // console.log('getCalendarAdminWorkItems res::', res)
      if (res.status === 200) {
        setTasks(res.data || [])
      }
    } catch (error) {
      console.error('fetchCalendarTasks error::', error)
      message.error('달력 업무 조회에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCalendarTasks()
  }, [scheduleBoardRefetch, teamGroupId])

  // 트래커 보드 & 스케쥴 보드 refetch (루틴/Scheduled 업무 수정 시)
  const handleRefetchScheduleBoard = () => {
    setTrackerBoardRefetch((prev) => prev + 1)
    setScheduleBoardRefetch((prev) => prev + 1)
  }

  const handleOpenRoutineItemPopup = (id) => {
    setSelectedRoutineItemId(id)
    setIsRoutineItemPopupOpen(true)
  }

  const handleOpenScheduledItemPopup = (id) => {
    setSelectedScheduledItemId(id)
    setIsScheduledItemPopupOpen(true)
  }

  // 현재 월의 달력 데이터 생성
  const getCalendarDays = () => {
    const startOfMonth = currentDate.startOf('month')
    const endOfMonth = currentDate.endOf('month')
    const startDate = startOfMonth.startOf('week') // 주의 시작 (일요일)
    const endDate = endOfMonth.endOf('week') // 주의 끝 (토요일)

    const days = []
    let currentDay = startDate

    while (currentDay.isBefore(endDate) || currentDay.isSame(endDate, 'day')) {
      days.push(currentDay)
      currentDay = currentDay.add(1, 'day')
    }

    return days
  }

  // 특정 날짜의 업무 가져오기
  const getTasksForDate = (date) => {
    if (!tasks || !Array.isArray(tasks)) return []
    return tasks.filter((task) => {
      if (!task.scheduledDate) return false
      const taskDate = dayjs(task.scheduledDate)
      return taskDate.isSame(date, 'day')
    })
  }

  const calendarDays = getCalendarDays()
  const weekDays = ['일', '월', '화', '수', '목', '금', '토']

  return (
    <section
      className="standalone-schedule-container"
      style={{
        padding: '16px 32px',
        marginTop: '48px',
      }}
    >
      <SectionHeader title={title} subtitle={subtitle} />

      <div className="schedule-calendar-wrapper">
        <Spin spinning={isLoading}>
          <div className="calendar-header">
            <div className="calendar-month-year">
              {currentDate.format('YYYY년 M월')}
            </div>
          </div>

          <div className="calendar-grid">
            {/* 요일 헤더 */}
            <div className="calendar-weekdays">
              {weekDays.map((day, index) => (
                <div
                  key={day}
                  className={`calendar-weekday ${index === 0 ? 'sunday' : ''}`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* 날짜 그리드 */}
            <div className="calendar-days">
              {calendarDays.map((date, index) => {
                const isCurrentMonth = date.month() === currentDate.month()
                const isToday = date.isSame(dayjs(), 'day')
                const isSunday = date.day() === 0
                const dayTasks = getTasksForDate(date)

                return (
                  <div
                    key={index}
                    className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSunday ? 'sunday' : ''}`}
                  >
                    <div className="calendar-day-number">{date.date()}</div>
                    <div className="calendar-day-tasks">
                      {dayTasks.map((task, taskIndex) => {
                        const handleTaskClick = () => {
                          if (task.workType === 'SCHEDULED') {
                            handleOpenScheduledItemPopup(task.id)
                          } else if (task.workType === 'ROUTINE') {
                            handleOpenRoutineItemPopup(task.id)
                          }
                        }

                        return (
                          <div
                            key={taskIndex}
                            className="calendar-task-item"
                            onClick={handleTaskClick}
                          >
                            <TaskColorChip
                              color={task.colorCode || '#FFC700'}
                            />
                            <span className="calendar-task-text">
                              {task.title}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </Spin>
      </div>

      {/* 스케쥴 보드 모달 */}
      <StandaloneRoutineItemPopup
        id={selectedRoutineItemId}
        open={isRoutineItemPopupOpen}
        onClose={() => {
          setIsRoutineItemPopupOpen(false)
          setSelectedRoutineItemId(null)
        }}
        onSuccess={handleRefetchScheduleBoard}
      />
      <StandaloneScheduledTaskPopup
        id={selectedScheduledItemId}
        open={isScheduledItemPopupOpen}
        onClose={() => {
          setIsScheduledItemPopupOpen(false)
          setSelectedScheduledItemId(null)
        }}
        onSuccess={handleRefetchScheduleBoard}
      />
    </section>
  )
}

export default ScheduleBoardAdminSection
